import { createContext, useContext, useState, type ReactNode } from "react";
import { create, useStore } from "zustand";
import {
  readStoredActionCollapsed,
  readStoredActionTab,
  storeActionCollapsed,
  storeActionTab,
} from "./action-persistence";
import type { ActionTab } from "./action-tab";

interface ActionStore {
  collapsed: boolean;
  activeTab: ActionTab;
  pendingLaunch: number | null;
  aiChatOpen: boolean;
  setTab: (tab: ActionTab) => void;
  expand: () => void;
  collapse: () => void;
  toggleCollapse: () => void;
  launch: () => void;
  clearPendingLaunch: () => void;
  openAiChat: () => void;
  closeAiChat: () => void;
}

const createActionStore = () =>
  create<ActionStore>((set, get) => ({
    collapsed: readStoredActionCollapsed(),
    activeTab: readStoredActionTab(),
    pendingLaunch: null,
    aiChatOpen: false,
    setTab: (tab) => {
      storeActionTab(tab);
      set({ activeTab: tab });
    },
    expand: () => {
      storeActionCollapsed(false);
      set({ collapsed: false });
    },
    collapse: () => {
      storeActionCollapsed(true);
      set({ collapsed: true });
    },
    toggleCollapse: () => {
      const next = !get().collapsed;
      storeActionCollapsed(next);
      set({ collapsed: next });
    },
    launch: () => {
      storeActionCollapsed(false);
      storeActionTab("context");
      set((state) => ({
        collapsed: false,
        activeTab: "context",
        pendingLaunch: (state.pendingLaunch ?? 0) + 1,
      }));
    },
    clearPendingLaunch: () => set({ pendingLaunch: null }),
    openAiChat: () => set({ aiChatOpen: true }),
    closeAiChat: () => set({ aiChatOpen: false }),
  }));

type ActionStoreApi = ReturnType<typeof createActionStore>;

const ActionStoreContext = createContext<ActionStoreApi | null>(null);

export function ActionStoreProvider({ children }: { children: ReactNode }) {
  const [store] = useState<ActionStoreApi>(() => createActionStore());

  return (
    <ActionStoreContext.Provider value={store}>
      {children}
    </ActionStoreContext.Provider>
  );
}

export function useActionStore(): ActionStore;
export function useActionStore<T>(selector: (state: ActionStore) => T): T;

export function useActionStore<T>(selector?: (state: ActionStore) => T) {
  const store = useContext(ActionStoreContext);
  if (!store) {
    throw new Error("useActionStore must be used within ActionStoreProvider");
  }
  return useStore(store, selector ?? ((state) => state as T));
}
